import { useMutation } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useState } from "react"
import { toast } from "sonner"

import { sendCodeVerifyDelete } from "@/api"
import { DialogModal } from "@/components/shared"
import { Button, DialogDescription, DialogTitle } from "@/components/ui"
import { ConfirmOTPDeleteBank } from "@/features/earns/components/banks/otp-confirm"
import { EValidateUser } from "@/modules/helpers/enum"
import { BankInfoAccount } from "@/modules/types"

const OTP_EXPIRY_SECONDS = 90
const OTP_STORAGE_KEY = "@expired_time_otp_delete_bank"

interface DeleteConfirmProps {
  open: boolean
  onDelete: (code: string) => void
  onCancel: () => void
  data: BankInfoAccount
  isPending: boolean
}

export const DeleteConfirm = ({
  open,
  onDelete,
  onCancel,
  data,
  isPending
}: DeleteConfirmProps) => {
  const [showOTPModal, setShowOTPModal] = useState(false)
  const { mutate: sendCodeVerifyDeleteMutation, isPending: isSendingOTP } = useMutation({
    mutationFn: () => sendCodeVerifyDelete(),
    mutationKey: ["send_code_verify_delete"],
    onSuccess() {
      const expiryTime = dayjs(Date.now()).add(OTP_EXPIRY_SECONDS, "seconds").toString()
      localStorage.setItem(OTP_STORAGE_KEY, expiryTime)
      setShowOTPModal(true)
    },
    onError(error: any) {
      const type = error?.response?.data?.error?.type
      if (type?.includes(EValidateUser.YOU_CAN_ONLY_SEND_OTP_AFTER_60S)) {
        toast.error("Bạn chỉ có thể gửi mã xác minh sau 60 giây.")
        return
      }
      toast.error("Không thể gửi mã xác minh, vui lòng thử lại!")
    }
  })

  const handleDelete = () => {
    sendCodeVerifyDeleteMutation()
  }

  const handleCancel = () => {
    setShowOTPModal(false)
    onCancel()
  }

  const handleOTPSuccess = (code: string) => {
    setShowOTPModal(false)
    // Don't call onDelete here - delete API is already called in ConfirmOTPDeleteBank's config.onSuccess
    // Just close the modal and let parent component handle UI updates via query invalidation
    onDelete(code) // This is now just for parent to know deletion succeeded, not to call API again
  }

  const handleOTPClose = () => {
    setShowOTPModal(false)
  }

  return (
    <>
      <DialogModal
        open={open && !showOTPModal}
        onClose={onCancel}
        title={
          <>
            <div className="w-16 h-16 rounded-full bg-[#E5B80033] flex items-center justify-center mb-3">
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.6875 15.4876L14.6625 2.5751C14.025 1.7126 13.05 1.2251 12 1.2251C10.9125 1.2251 9.93753 1.7126 9.33753 2.5751L1.31253 15.4876C0.562527 16.5001 0.450027 17.8126 1.01253 18.9376C1.57503 20.0626 2.70003 20.7751 3.97503 20.7751H20.025C21.3 20.7751 22.425 20.0626 22.9875 18.9376C23.55 17.8501 23.4375 16.5001 22.6875 15.4876ZM21.4875 18.1876C21.1875 18.7501 20.6625 19.0876 20.025 19.0876H3.97503C3.33753 19.0876 2.81253 18.7501 2.51253 18.1876C2.25003 17.6251 2.28753 16.9876 2.66253 16.5001L10.6875 3.5876C10.9875 3.1751 11.475 2.9126 12 2.9126C12.525 2.9126 13.0125 3.1376 13.3125 3.5876L21.3375 16.5001C21.7125 16.9876 21.75 17.6251 21.4875 18.1876Z"
                  fill="#E6B800"
                />
                <path
                  d="M12.0002 8.2002C11.5502 8.2002 11.1377 8.5752 11.1377 9.0627V13.1502C11.1377 13.6002 11.5127 14.0127 12.0002 14.0127C12.4877 14.0127 12.8627 13.6377 12.8627 13.1502V9.0252C12.8627 8.5752 12.4502 8.2002 12.0002 8.2002Z"
                  fill="#E6B800"
                />
                <path
                  d="M12.0002 15C11.5502 15 11.1377 15.375 11.1377 15.8625V16.05C11.1377 16.5 11.5127 16.9125 12.0002 16.9125C12.4877 16.9125 12.8627 16.5375 12.8627 16.05V15.825C12.8627 15.375 12.4502 15 12.0002 15Z"
                  fill="#E6B800"
                />
              </svg>
            </div>

            {/* Title */}
            <DialogTitle className="text-xl font-bold text-gray-900 mb-3">
              Xóa thông tin tài khoản
            </DialogTitle>

            {/* Description */}
            <DialogDescription className="text-black text-sm leading-relaxed mb-3">
              Bạn muốn gỡ bỏ thông tin tài khoản ngân hàng "{data?.banks?.bankName} ••••{" "}
              {data?.accountNumber.slice(-4)}"
            </DialogDescription>
          </>
        }
        children={
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1 border-gray-300 text-gray-700 bg-white"
              onClick={handleCancel}
            >
              Quay lại
            </Button>
            <Button
              className="flex-1 text-white font-normal bg-[#E5B800]"
              onClick={handleDelete}
              disabled={isPending || isSendingOTP}
            >
              {isSendingOTP ? "Đang gửi OTP..." : "Xóa"}
            </Button>
          </div>
        }
      />
      <ConfirmOTPDeleteBank
        visible={showOTPModal}
        onClose={handleOTPClose}
        onSuccess={(code: string) => handleOTPSuccess(code)}
        data={{ bankId: data.id }}
      />
    </>
  )
}
